import {Fragment, ReactElement, useEffect, useState} from "react";
import { IUnitType, IUnitTypeMetrics, IUseRevenueWizardState } from '../logic/useRevenueWizard';
import {getMonths} from "../../../utils/months-helper";
import * as css from "../styles/summary-table.module.scss";
import {Button} from "@zendeskgarden/react-buttons";
import {round} from "../../../utils/math";
import * as stepCSS from "../styles/revenue-wizard.module.scss";
import RoundingControls from "./RoundingControls";
import VZTwoButtonGroup from "../../vz-ui/vz-two-button-group/VZTwoButtonGroup";


interface ISummaryTableProps {
    revenueWizardInstance: IUseRevenueWizardState;
}

// TODO @bowman: I slammed this in to give us something to work with for a first pass, but I'd like to look
// at refactoring it with Doug's simpleHotTable component after it makes it into the codebase
export default function SummaryTable({revenueWizardInstance}: ISummaryTableProps): ReactElement {
    const {
        property,
        unitTypeData,
        selectedMetricToFavor: favorMetric,
        countToRate,
        convertValuesWithFavor,
        getUnitTypeVersionSummary,
        setSelectedMetricToFavor,
    } = revenueWizardInstance;

    const monthIndexToStartFrom = property.reforecastStartMonthIndex == 0 ? 0 : property.reforecastStartMonthIndex - 1;
    const metricLabels: Map<keyof IUnitTypeMetrics, string> = new Map([
        ["expirationCount", "Expirations"],
        ["occupancyCount", "Occupancy"],
        ["renewalCount", "Renewals"],
        ["moveOutCount", "Move Outs"],
        ["moveInCount", "Move Ins"],
    ]);

    const [showAsRates, setShowAsRates] = useState(true);
    const [unitTypes, setUnitTypes] = useState<IUnitType[]>([]);

    useEffect(() => {
        setUnitTypes(convertValuesWithFavor(unitTypeData, favorMetric));
    }, [unitTypeData, favorMetric]);

    function getRfcstHeaderLabel(monthIndex: number): string {
        if (property.reforecastStartMonthIndex == 0 && monthIndex == 0) {
            return "RFCST";
        }

        if (monthIndex == 0) {
            return "ACTUALS";
        }

        if (monthIndex == 1) {
            return "RFCST";
        }

        return "";
    }

    function tableCell(val: number | string, isActuals: boolean) {
        return (
            <td className={`${isActuals && css.actualsCell} ${Number(val) < 0 && css.errorCell}`}>
                {val}
            </td>
        );
    }

    // used for occupancy, expirations, and move ins
    function metricMonthOutput(unitType: IUnitType, metric: keyof IUnitTypeMetrics) {
        if (!unitType.rfcst[metric].length || !unitType.bdgt[metric].length) {
            return;
        }

        const rfcstAverage = round(unitType.rfcst[metric].average(), 1);
        const bdgtAverage = round(unitType.bdgt[metric].average(), 1);

        const getDisplayValue = (count: number): string => {
            count = round(count, 1);
            let displayVal: string | number = count;
            if (metric == "occupancyCount" && showAsRates) {
                displayVal = `${round(countToRate(count, unitType.unitCount), 1)}%`;
            }
            return String(displayVal);
        };

        return (
            <>
                <td className={css.metricNameCell}>
                    {metricLabels.get(metric)}
                </td>

                {/* rfcst monthly values */}
                {unitType.rfcst[metric].slice(monthIndexToStartFrom).map((count, i) => {
                    if (metric == "moveInCount" && i == 0) {
                        return <td key={i} className={css.actualsCell}></td>;
                    }
                    return <Fragment key={i}>{tableCell(getDisplayValue(count), monthIndexToStartFrom == 0 ? false : i == 0)}</Fragment>;
                })}

                {/* rfcst summary averages */}
                <td className={css.summaryCell}>
                    {getDisplayValue(rfcstAverage)}
                </td>

                {/* bdgt monthly values */}
                {unitType.bdgt[metric].map((count, i) => <Fragment key={i}>{tableCell(getDisplayValue(count), false)}</Fragment>)}

                {/* bdgt summary averages */}
                <td className={css.summaryCell}>
                    {getDisplayValue(bdgtAverage)}
                </td>
            </>
        );
    }

    // since we merged these two metrics into a single row I'm just rendering it with a different function
    function renewalsAndMoveOutsOutput(unitType: IUnitType) {
        const rfcstExpirationsTotal = unitType.rfcst.expirationCount.sum();
        const bdgtExpirationsTotal = unitType.bdgt.expirationCount.sum();

        const rfcstRenewalsTotal = unitType.rfcst.renewalCount.sum();
        const rfcstMoveOutsTotal = unitType.rfcst.moveOutCount.sum();
        const bdgtRenewalsTotal = unitType.bdgt.renewalCount.sum();
        const bdgtMoveOutsTotal = unitType.bdgt.moveOutCount.sum();

        const getDisplayValue = (count: number, expirations: number): string => {
            return showAsRates ? `${round(countToRate(count, expirations), 1)}%` : String(round(count, 1));
        };

        return (
            <>
                <td className={`${css.multiLineCell} ${css.metricNameCell}`}>
                    <span className={css.largeText}>Renewals</span>
                    <span className={css.smallText}>Move Outs</span>
                </td>
                {/* rfcst monthly values */}
                {unitType.rfcst.renewalCount.slice(monthIndexToStartFrom).map((renewalCount, i) =>
                    <td key={i} className={`${css.multiLineCell} ${i == 0 && property.reforecastStartMonthIndex != 0 && css.actualsCell}`}>
                        <span className={css.largeText}>
                            {getDisplayValue(renewalCount, unitType.rfcst.expirationCount[i + monthIndexToStartFrom] ?? 0)}
                        </span>
                        <span className={css.smallText}>
                            {getDisplayValue(unitType.rfcst.moveOutCount[i + monthIndexToStartFrom] ?? 0, unitType.rfcst.expirationCount[i + monthIndexToStartFrom] ?? 0)}
                        </span>
                    </td>
                )}
                {/* rfcst summary averages */}
                <td className={`${css.multiLineCell} ${css.summaryCell}`}>
                    <span className={css.largeText}>
                        {getDisplayValue(rfcstRenewalsTotal, rfcstExpirationsTotal)}
                    </span>
                    <span className={css.smallText}>
                        {getDisplayValue(rfcstMoveOutsTotal, rfcstExpirationsTotal)}
                    </span>
                </td>
                {/* bdgt monthly values */}
                {unitType.bdgt.renewalCount.map((renewalCount, i) =>
                    <td key={i} className={css.multiLineCell}>
                        <span className={css.largeText}>
                            {getDisplayValue(renewalCount, unitType.bdgt.expirationCount[i] ?? 0)}
                        </span>
                        <span className={css.smallText}>
                            {getDisplayValue(unitType.bdgt.moveOutCount[i] ?? 0, unitType.bdgt.expirationCount[i] ?? 0)}
                        </span>
                    </td>
                )}
                {/* bdgt summary averages */}
                <td className={`${css.multiLineCell} ${css.summaryCell}`}>
                    <span className={css.largeText}>
                        {getDisplayValue(bdgtRenewalsTotal, bdgtExpirationsTotal)}
                    </span>
                    <span className={css.smallText}>
                        {getDisplayValue(bdgtMoveOutsTotal, bdgtExpirationsTotal)}
                    </span>
                </td>
            </>
        );
    }

    return (
        <div className={stepCSS.stepContainer}>
            <h3 className={stepCSS.stepTitle}>Preview Your Data</h3>
            <p className={stepCSS.stepParagraph}>
                Give everything a final once-over to make sure it all looks good before we proceed.
            </p>

            <div className={css.summaryTable}>
                <div className={css.header}>
                    <RoundingControls
                        revenueWizardInstance={revenueWizardInstance}
                        standalone
                        tooltipPlacement="bottom"
                    />
                    <VZTwoButtonGroup
                        fieldLabel={"Favoring"}
                        firstLabel={"Renewals"}
                        secondLabel={"Occupancy"}
                        onChange={(isRenewalsSelected => setSelectedMetricToFavor(isRenewalsSelected ? "renewals" : "occupancy"))}
                        inline
                    />
                    <div style={{marginLeft: "auto"}}>
                        <VZTwoButtonGroup
                            fieldLabel={"View As"}
                            firstLabel={"Percents"}
                            secondLabel={"Counts"}
                            onChange={setShowAsRates}
                            inline
                        />
                    </div>
                </div>

                <div className={css.tableWrapper}>
                    <table>
                        <thead>
                            <tr>
                                <th className={css.labelHeader}>Unit Type</th>
                                <th className={css.metricNameHeader}>Metric</th>
                                {
                                    getMonths(property).slice(monthIndexToStartFrom).map((x, i) =>
                                        <th key={i} className={`
                                            ${css.multiLineCell}
                                            ${i == 0 && css.actualsCell}
                                        `}>
                                            <span className={css.smallText}>{getRfcstHeaderLabel(i)}</span>
                                            <span className={css.largeText}>{x}</span>
                                        </th>
                                    )
                                }
                                <th className={`${css.multiLineCell} ${css.thickBorderRight}`}>
                                    <span className={css.smallText}>RFCST</span>
                                    <span className={css.largeText}>Average</span>
                                </th>
                                {
                                    getMonths(property).map((x, i) =>
                                        <th key={i} className={css.multiLineCell}>
                                            <span className={css.smallText}>&nbsp;{i == 0 ? "BDGT" : ""}</span>
                                            <span className={css.largeText}>{x}</span>
                                        </th>
                                    )
                                }
                                <th className={css.multiLineCell}>
                                    <span className={css.smallText}>BDGT</span>
                                    <span className={css.largeText}>Average</span>
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {
                                [getUnitTypeVersionSummary(unitTypes), ...unitTypes].map(unitType =>
                                    <Fragment key={unitType.name}>
                                        <tr className={css.unitTypeStartRow}>
                                            <td rowSpan={4} className={`${css.multiLineCell} ${css.labelCell}`}>
                                                <span className={css.largeText}>{unitType.name}</span>
                                                <span className={css.smallText}>{unitType.unitCount} Units</span>
                                            </td>
                                            {metricMonthOutput(unitType, "expirationCount")}
                                        </tr>
                                        <tr>
                                            {renewalsAndMoveOutsOutput(unitType)}
                                        </tr>
                                        <tr>
                                            {metricMonthOutput(unitType, "occupancyCount")}
                                        </tr>
                                        <tr>
                                            {metricMonthOutput(unitType, "moveInCount")}
                                        </tr>
                                    </Fragment>
                                )
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
